/*
 * asr-channel-split.js — распознавать многоканальную запись ПО КАНАЛАМ,
 * а не сводя её в моно.
 *
 * Зачем: в моно-сведении голоса накладываются, и на перекрытии Whisper
 * съедает хвост реплики. Разделив каналы, мы даём модели чистый монолог —
 * и заодно получаем «кто говорил» без модели диаризации.
 *
 * ⚠️ Каждое решение здесь ИЗМЕРЕНО на корпусе AMI (CC-BY: настоящая речь,
 * настоящие перебивания, отдельный микрофон на говорящего плюс готовое
 * моно-сведение) браузерным движком transformers.js, модель base.
 * Мера — F1 по словам, а НЕ полнота: полнота одна переворачивает вывод.
 *
 *   моно-сведение (как было)       полнота 56.9%  точность 88.4%  F1 69.2%
 *   по каналам, без обработки      полнота 86.1%  точность 52.6%  F1 65.3%  ← ХУЖЕ
 *   по каналам + пропуск + дедуп   полнота 71.7%  точность 81.6%  F1 76.3%  ← +7.1 п.п.
 *
 * ⚠️⚠️ Ловушка, ради которой это и написано: «по каналам, без обработки»
 * даёт +29 п.п. ПОЛНОТЫ и при этом делает выдачу ХУЖЕ. Кто померит только
 * полноту, отгрузит ухудшение, будучи уверенным в улучшении.
 */
(function (global) {
    'use strict';

    /* Порог корреляции каналов. Измерено на живой записи: у РАЗВЕДЁННЫХ
       каналов (разные говорящие) корреляция всех шести пар не превысила
       0.0245 по модулю, у моно в стерео-обёртке она ровно 1.0. Популяции
       разнесены предельно далеко, поэтому годится любой порог 0.3…0.8. */
    var CORR_MAX = 0.5;

    /* Меньше секунды речи в канале — канал пустой, и распознавать его нельзя:
       на тишине Whisper сочиняет. Замерено: пустой канал выдал 226 слов
       связного, но выдуманного текста. Вклад пропуска: +1.5 п.п. F1. */
    var MIN_SPEECH_SEC = 1;

    /* Похожесть текстов, при которой сегменты разных каналов считаются одной
       и той же речью. Развёртка 0.2…0.5 дала гладкий максимум на 0.4
       (F1 70.8 / 71.5 / 74.5 / 75.1 / 76.3 / 75.2). */
    var DUP_SIM = 0.4;

    function words(s) {
        return String(s || '').toLowerCase()
            .replace(/[^\p{L}\p{N}' ]/gu, ' ').split(/\s+/).filter(Boolean);
    }

    /* Доля общих слов относительно КОРОТКОГО из двух наборов: наводка часто
       ловится обрывком, и деление на длинный текст занижало бы сходство. */
    function similarity(a, b) {
        if (!a.length || !b.length) return 0;
        var A = new Set(a), B = new Set(b), n = 0;
        B.forEach(function (w) { if (A.has(w)) n++; });
        return n / Math.min(A.size, B.size);
    }

    function rms(pcm, a, b, sampleRate) {
        var i0 = Math.max(0, Math.floor(a * sampleRate));
        var i1 = Math.min(pcm.length, Math.ceil(b * sampleRate));
        if (i1 <= i0) return 0;
        var s = 0;
        for (var i = i0; i < i1; i++) s += pcm[i] * pcm[i];
        return Math.sqrt(s / (i1 - i0));
    }

    var API = {
        CORR_MAX: CORR_MAX,
        MIN_SPEECH_SEC: MIN_SPEECH_SEC,
        DUP_SIM: DUP_SIM,

        /* Корреляция пары каналов без отдельного прохода по сигналу:
           ΣLR = ΣM² − ΣS² — тождество, а не приближение. */
        correlation: function (L, R) {
            var n = Math.min(L.length, R.length), eM = 0, eS = 0, eL = 0, eR = 0;
            for (var i = 0; i < n; i++) {
                var l = L[i], r = R[i], m = (l + r) / 2, s = (l - r) / 2;
                eM += m * m; eS += s * s; eL += l * l; eR += r * r;
            }
            return (eL > 0 && eR > 0) ? (eM - eS) / Math.sqrt(eL * eR) : 1;
        },

        /* Стоит ли вообще разделять. Каналы считаются разведёнными, если
           МАКСИМУМ модуля корреляции по всем парам ниже порога: одна
           скоррелированная пара означает, что это моно в многоканальной
           обёртке, а не разные говорящие. */
        shouldSplit: function (channels, opts) {
            opts = opts || {};
            var pairs = [], max = 0;
            if (!channels || channels.length < 2) return { split: false, maxCorr: 1, pairs: pairs };
            for (var i = 0; i < channels.length; i++) {
                for (var j = i + 1; j < channels.length; j++) {
                    var c = API.correlation(channels[i], channels[j]);
                    pairs.push({ a: i, b: j, corr: c });
                    if (Math.abs(c) > max) max = Math.abs(c);
                }
            }
            var lim = (typeof opts.corrMax === 'number') ? opts.corrMax : CORR_MAX;
            return { split: max < lim, maxCorr: max, pairs: pairs };
        },

        /* Канал, в котором почти нет речи, распознавать нельзя.
           speechSec приходит из VAD (silero-vad-worker → totalSpeechSec). */
        keepChannel: function (speechSec, opts) {
            var min = (opts && typeof opts.minSpeechSec === 'number') ? opts.minSpeechSec : MIN_SPEECH_SEC;
            return (+speechSec || 0) >= min;
        },

        /* Слить сегменты каналов в один транскрипт, сняв межканальные дубли.
           parts: [{ ch:Number, pcm:Float32Array, segments:[{start,end,text}] }]
           Возвращает сегменты по времени, каждый с полем speaker. */
        merge: function (parts, opts) {
            opts = opts || {};
            var sr = opts.sampleRate || 16000;
            var sim = (typeof opts.dupSim === 'number') ? opts.dupSim : DUP_SIM;
            var label = opts.label || function (ch) { return 'Speaker ' + (ch + 1); };

            var all = [], p, s;
            for (p = 0; p < parts.length; p++) {
                var part = parts[p], segs = part.segments || [];
                for (s = 0; s < segs.length; s++) {
                    var t = String(segs[s].text || '').trim();
                    if (!t) continue;
                    var st = +segs[s].start || 0;
                    var en = (segs[s].end != null && +segs[s].end > st) ? +segs[s].end : st + 2;
                    all.push({ ch: part.ch, pcm: part.pcm, start: st, end: en, text: t, w: words(t) });
                }
            }
            all.sort(function (a, b) { return a.start - b.start; });

            /* Дубль = пересечение по времени + похожий текст на РАЗНЫХ каналах.
               Оставляем канал, где эта речь ГРОМЧЕ: там настоящий говорящий,
               на остальных это акустическая наводка.
               ⚠️ Низкая корреляция НЕ означает отсутствия наводок — замерено:
               при корреляции 0.02 речь протекала во все микрофоны, и 71.5%
               «лишних» слов оказались повтором той же речи с соседнего
               канала, а вовсе не выдумкой модели. */
            var dropped = {}, i, j;
            for (i = 0; i < all.length; i++) {
                if (dropped[i]) continue;
                for (j = i + 1; j < all.length && all[j].start < all[i].end; j++) {
                    if (dropped[j] || all[j].ch === all[i].ch) continue;
                    if (similarity(all[i].w, all[j].w) < sim) continue;
                    var ea = all[i].pcm ? rms(all[i].pcm, all[i].start, all[i].end, sr) : 1;
                    var eb = all[j].pcm ? rms(all[j].pcm, all[j].start, all[j].end, sr) : 0;
                    if (ea >= eb) { dropped[j] = 1; }
                    else { dropped[i] = 1; break; }
                }
            }

            var out = [];
            for (var k = 0; k < all.length; k++) {
                if (dropped[k]) continue;
                out.push({
                    start: all[k].start, end: all[k].end, text: all[k].text,
                    channel: all[k].ch, speaker: label(all[k].ch),
                });
            }
            return { segments: out, dropped: Object.keys(dropped).length, total: all.length };
        },
    };

    if (typeof global !== 'undefined') global.AsrChannelSplit = API;
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
})(typeof window !== 'undefined' ? window : this);
